import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { LANGUAGE_GROUPS } from "@/data/content";
import Flag from "@/components/Flag";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Counter from "@/components/Counter";

export default function Languages() {
  const [query, setQuery] = useState("");

  const totalCount = useMemo(
    () => LANGUAGE_GROUPS.reduce((acc, g) => acc + g.items.length, 0),
    []
  );

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return LANGUAGE_GROUPS;
    const q = query.toLowerCase();
    return LANGUAGE_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((i) => i.name.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <div data-testid="languages-page" className="bg-white">
      <Seo
        title="85+ Languages We Speak"
        description="From Spanish and Mandarin to Haitian Creole and rare African dialects — Petraloc covers 85+ languages with native specialists and certified linguists."
        path="/languages"
      />

      {/* HERO */}
      <section className="relative pt-16 lg:pt-[68px] bg-[#071D49] text-white overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-[#00D1B2]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#1A4E8A]/30 blur-3xl" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 py-14 lg:py-18 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal variant="right" className="lg:col-span-7">
            <p className="eyebrow-teal">Languages</p>
            <h1 className="mt-4 text-4xl lg:text-[56px] font-semibold tracking-tight leading-[1.05] text-white">
              <Counter end={85} suffix="+" duration={1600} /> languages.<br />
              One delivery standard.
            </h1>
            <p className="mt-5 text-[15px] text-white/75 leading-relaxed max-w-2xl">
              Native specialists in major world languages, Caribbean Creole, and the
              underserved African, Asian, and Middle Eastern markets generic providers skip.
            </p>
          </Reveal>

          <Reveal variant="left" className="lg:col-span-5">
            <div className="bg-white/[0.04] border border-white/10 rounded-md p-5 backdrop-blur">
              <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/55 mb-3">
                Search the catalog
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try Haitian Creole, Mandarin, Wolof…"
                  data-testid="lang-search-input"
                  className="w-full bg-white/[0.04] border border-white/15 rounded-md pl-10 pr-3 py-3 text-[14px] text-white placeholder-white/40 focus:outline-none focus:border-[#00D1B2] transition-colors"
                />
              </div>
              <p className="mt-3 text-[12px] text-white/55">
                Don't see one? Most rare dialects are available on request.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LANGUAGE GROUPS — dense */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          {filteredGroups.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-[15px] text-[#5B6472]">
                No languages match "<span className="text-[#071D49] font-medium">{query}</span>".
                Reach out — we likely cover it on request.
              </p>
            </div>
          ) : (
            <div className="space-y-10 lg:space-y-12">
              {filteredGroups.map((group) => (
                <Reveal variant="up" key={group.title}>
                  <div>
                    <div className="flex items-baseline justify-between border-b border-[#E5E7EB] pb-3 mb-5">
                      <div>
                        <h2 className="text-xl lg:text-[22px] font-semibold text-[#071D49] tracking-tight">
                          {group.title}
                        </h2>
                        {group.subtitle && (
                          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#00D1B2] mt-1">
                            {group.subtitle}
                          </p>
                        )}
                      </div>
                      <span className="text-[12px] text-[#5B6472]">
                        {group.items.length} {group.items.length === 1 ? "language" : "languages"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {group.items.map((lang) => (
                        <div
                          key={`${group.title}-${lang.name}`}
                          data-testid={`lang-item-${lang.name.toLowerCase().replace(/\s/g, "-")}`}
                          className="ent-card px-3 py-2.5 flex items-center gap-2.5 text-[12.5px] text-[#071D49]"
                        >
                          <Flag code={lang.code} className="w-5 h-3.5 flex-shrink-0 rounded-sm" />
                          <span className="truncate font-medium">{lang.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
